import { APP_DEEPLINK_WEBOUTSIDE_URL, MIXPANEL_EVENTS } from '../../constants';
import { useResponsesContext } from '../../context/ResponseContext';
import {
  BotMessageOptionProps,
  EOptionAction,
  ETicket,
  OptionsProps,
  QueryResponse,
} from '../../types';
import { Button } from 'ui/mobile-ds';

import React from 'react';
import { track } from '../../utils/track';
import { invokeNativeBridge } from '../../utils/messageUtils';
import { useAppContext } from '../../context/AppContext';
import AppConfig from '../../AppConfig';
import clsx from 'clsx';

const handleAddButtonResponseToChat = async (
  title: string,
  queryIdRef: React.MutableRefObject<number>,
  setResponses: React.Dispatch<React.SetStateAction<QueryResponse[]>>,
  setIsAnswerLoading: (value: number | null) => void,
  setOptionClicked: (value: React.SetStateAction<boolean>) => void,
  getAnswer: (messages: string, queryId: number) => Promise<string | undefined>,
) => {
  const queryId = queryIdRef.current++;

  // Add the response to the state
  setResponses((prev) => [
    ...prev,
    {
      id: queryId,
      query: title,
      response: '',
      feedbackGiven: true,
      cid: '',
      date: new Date(),
    },
  ]);

  // Set loading state
  setIsAnswerLoading(queryId);

  // Hide the option div
  setOptionClicked(true);

  // Call the API to get the response
  await getAnswer(title, queryId);
};

const BotMessageOption: React.FC<BotMessageOptionProps> = ({
  title,
  handleButtonClick,
  isDisabled,
}) => {
  return (
    <Button
      disabled={isDisabled}
      loading={false}
      type="submit"
      onClick={handleButtonClick}
      className={clsx({
        '!min-w-0 rounded-lg border-mobile-gray-100 border-[1.5px] w-fit py-[6px] px-2 text-sm bg-white text-gray-500 font-semibold max-w-[82%] text-left hover:bg-mobile-gray-100 focus:bg-mobile-gray-100 disabled:bg-white':
          true,
        'border-gray-300 bg-white text-gray-400 cursor-not-allowed hover:bg-white focus:bg-white active:bg-white':
          isDisabled,
        'hover:bg-mobile-gray-100 focus:bg-mobile-gray-100': !isDisabled,
      })}
    >
      {title}
    </Button>
  );
};

const Options: React.FC<OptionsProps> = ({
  options,
  setOptionClicked,
  getAnswer,
  conversationId,
  isDisabled,
}) => {
  const { setResponses, queryIdRef, setIsAnswerLoading, variantConfig } =
    useResponsesContext();

  const { isWebView } = useAppContext();

  const handleButtonClick = async (content: string) => {
    track({
      event: MIXPANEL_EVENTS.BOT_MENU_CLICKED,
      data: {
        buttonText: content,
        menuActionType: EOptionAction.BOT_BUTTON,
        menuTitle: content,
        bot: variantConfig?.variant,
      },
    });
    await handleAddButtonResponseToChat(
      content,
      queryIdRef,
      setResponses,
      setIsAnswerLoading,
      setOptionClicked,
      getAnswer,
    );
  };

  const handleDeeplinkButtonClick = async (content: string, title: string) => {
    track({
      event: MIXPANEL_EVENTS.BOT_MENU_CLICKED,
      data: {
        buttonText: title,
        conversationId,
        menuActionType: EOptionAction.DEEP_LINK,
        bot: variantConfig?.variant,
      },
    });
    if (isWebView && content.includes('deeplink')) {
      invokeNativeBridge('callDeeplink', content);
    } else if (isWebView) {
      invokeNativeBridge(
        'callDeeplink',
        `${APP_DEEPLINK_WEBOUTSIDE_URL}${encodeURIComponent(content)}`,
      );
    } else {
      window.open(content);
    }

    await handleAddButtonResponseToChat(
      title,
      queryIdRef,
      setResponses,
      setIsAnswerLoading,
      setOptionClicked,
      getAnswer,
    );
  };

  const handleRaiseATicketButton = async (
    masterCategoryTag: string,
    title: string,
  ) => {
    const url = `${AppConfig.SUPPORT_CENTER_BASE_URL}/support/raise-a-query/chat?source=CHAT&${conversationId}`;
    const imtcUrl = `${AppConfig.SUPPORT_CENTER_BASE_URL}/support/raise-a-imtc-query/chat?source=CHAT&${conversationId}`;
    await handleAddButtonResponseToChat(
      title,
      queryIdRef,
      setResponses,
      setIsAnswerLoading,
      setOptionClicked,
      getAnswer,
    );
    if (masterCategoryTag === ETicket.IS_MY_TREATMENT_COVERED_IMTC_AT) {
      track({
        event: MIXPANEL_EVENTS.IMTC_RAISE_A_TICKET_CLICKED,
        data: {
          buttonText: title,
          conversationId,
          menuActionType: EOptionAction.TICKET,
          bot: variantConfig?.variant,
        },
      });
      window.open(imtcUrl, '_self');
    } else {
      track({
        event: MIXPANEL_EVENTS.RAISE_A_TICKET_CLICKED,
        data: {
          buttonText: title,
          conversationId,
          menuActionType: EOptionAction.TICKET,
          bot: variantConfig?.variant,
        },
      });
      window.open(url, '_self');
    }
  };

  return (
    <div className="flex flex-wrap relative w-full pl-[50px] py-4 bg-white gap-2">
      {(options ?? []).map((option, index) => {
        switch (option.actionType) {
          case EOptionAction.BOT_BUTTON:
            return (
              <BotMessageOption
                key={index}
                title={option.title}
                handleButtonClick={() => handleButtonClick(option.title)}
                setOptionClicked={setOptionClicked}
                isDisabled={isDisabled}
              />
            );
          case EOptionAction.DEEP_LINK:
            return (
              <BotMessageOption
                key={index}
                title={option.title}
                handleButtonClick={() =>
                  option.content
                    ? handleDeeplinkButtonClick(option.content, option.title)
                    : null
                }
                setOptionClicked={setOptionClicked}
                isDisabled={isDisabled}
              />
            );
          case EOptionAction.TICKET:
            return (
              <BotMessageOption
                key={index}
                title={option.title}
                handleButtonClick={() => {
                  // Ensure this is called
                  handleRaiseATicketButton(
                    option.master_category_tag || '',
                    option.title,
                  );
                }}
                setOptionClicked={setOptionClicked}
                isDisabled={isDisabled}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default Options;
